import {PriceFunc, PriceElemsData, PriceSegment, getListPrice} from '@icc/price';
import { CustomPricesRecords } from '@icc/shared/custom-price/CustomPrice';
import {APP_CONFIG, AppConfig, AppConfigFactory} from '@icc/shared/config';;
import {Injectable, Inject} from '@angular/core';
import { core } from '@icc/shared/helpers';
import { Common } from '@icc/shared/helpers';

/**
 * Factory dopłat za system.
 *
 * @export
 * @param {object} Core Core
 * @return {object} Factory
 */
@Injectable()
export class PriceSystemService {

    constructor(@Inject(APP_CONFIG) private config: AppConfigFactory) {}

    /**
     * Dopłata procentowa liczona wyłącznie od bazowej ceny rastra.
     */
    @PriceFunc({
        shortName: 'systemBasePriceSurcharge',
        data: {
            system: 'conf.System',
            multiplier: 'multipliers',
            dealerId: 'dealerId',
        },
    })
    suppSystemBasePriceSurcharge(
        { PriceStack }: PriceElemsData,
        { system, multiplier, dealerId }: { system: any; multiplier: any; dealerId: number }
    ): PriceSegment[] {
        const priceFactor = parseFloat(system?.price_factor_to_base);
        if (!priceFactor || !Common.isArray(PriceStack)) {
            return [];
        }

        const sashSegments = PriceStack.filter(
            segment => segment && segment.type === 'sashes' && segment.valueType === 'value'
        );
        if (!sashSegments.length) {
            return [];
        }

        const basePrice = sashSegments.reduce(
            (sum, segment) => sum + parseFloat(segment.baseValue as any),
            0
        );
        if (!Number.isFinite(basePrice)) {
            return [];
        }

        const multipliersSource = multiplier?.[dealerId] || multiplier;
        const systemMultiplier =
            parseFloat(multipliersSource?.WindowLine?.[system.id]) || 1;
        const surcharge = basePrice * priceFactor / 100 * systemMultiplier;
        return [{
            type: 'systemBasePriceSurcharge',
            baseValue: surcharge,
            value: surcharge,
            valueType: 'value',
            excludeFromPriceFactors: true,
            data: {
                id: system.id,
                name: system.name,
                type: system.type,
                priceFactor,
            },
        }];
    }

    /**
     * Dopłata procentowa za system.
     *
     * @param {number} price          Cena wejściowa.
     * @param {object} PriceElems     Składowe ceny
     * @param {array}  NoPriceCauses  Powody braku wyceny.
     * @param {array}  constrElems    Lista elementów konstrukcyjnych, do przeliczenia przez dopłatę.
     * @param {object} system         System.
     * @param {bool}   sum            Czy dopłata za system na być sumowana do dopłaty za kształt.
     * @param {object} multiplier     Jeżeli dealer ma ustawiony mnoznik to uzyj zamiast wpolczynnika
     * @return {number} Cena z doliczoną dopłatą.
     */
    @PriceFunc({
        shortName: 'system',
        data: {
            system     : 'conf.System',
            multiplier : 'multipliers',
            customPrice: 'price.WindowLine',
            dealerId   : 'dealerId',
            marketId   : 'marketId'
        }
    })
    suppSystem({listPrice}: PriceElemsData,
        {system, multiplier, customPrice, dealerId}: {system: any; multiplier: any; customPrice: CustomPricesRecords, dealerId}
    ): PriceSegment[] {
        let factors      = 1;
        let systemFactor = 1;
        const listPriceEnableInMarket = this.config().IccConfig.Offer.listPrice ? this.config().listPriceEnableInMarket : true;

        if (Common.isObject(multiplier)
            && Common.isDefined(multiplier[dealerId])
            && Common.isDefined(multiplier[dealerId].WindowLine)
            && Common.isDefined(multiplier[dealerId].WindowLine[system.id])
            && !this.config().IccConfig.Dealer.surchargeMultipliers
        ) {
            systemFactor = core.num(multiplier[dealerId].WindowLine[system.id]) ? core.num(multiplier[dealerId].WindowLine[system.id]) : 1;
        } else if (Common.isObject(multiplier)
            && Common.isDefined(multiplier.WindowLine)
            && Common.isDefined(multiplier.WindowLine[system.id])
            && !this.config().IccConfig.Dealer.surchargeMultipliers
        ) {
            systemFactor = core.num(multiplier.WindowLine[system.id]) ? core.num(multiplier.WindowLine[system.id]) : 1;
        } else if (Common.isObject(customPrice) && customPrice[system.id]) {
            systemFactor = (customPrice[system.id].getPrice('price_factor') + 100) / 100;
        } else {
            systemFactor = (parseFloat(getListPrice(system, 'price_factor', listPrice, listPriceEnableInMarket)) + 100) / 100;
        }
        factors *= (systemFactor);
        return [{
            type: 'system',
            baseValue: systemFactor,
            value: systemFactor,
            valueType: 'percent',
            data: {
                id: system.id,
                name: system.name,
                type: system.type
            }
        }];
    }
}
